import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {StructuredNotificationMessage} from '../_helpers/entity-notification';

export type NotificationColor = 'success' | 'warning' | 'danger';

export interface NotificationItem {
  id: number;
  message: string;
  details?: string[];
  route?: string;
  color: NotificationColor;
  dateTime: Date;
  read: boolean;
}

@Injectable({providedIn: 'root'})
export class NotificheService {
  public notifiche: BehaviorSubject<Array<NotificationItem>> = new BehaviorSubject([]);
  public notificheAlert: BehaviorSubject<Array<NotificationItem>> = new BehaviorSubject([]);
  private nextNotificationId = 1;
  private popupTimeout?: ReturnType<typeof setTimeout>;

  clearAll() {
    if (this.popupTimeout) {
      clearTimeout(this.popupTimeout);
      this.popupTimeout = undefined;
    }

    this.notifiche.next([]);
    this.notificheAlert.next([]);
    this.nextNotificationId = 1;
  }

  clearPopup() {
    if (this.popupTimeout) {
      clearTimeout(this.popupTimeout);
      this.popupTimeout = undefined;
    }

    this.notificheAlert.next([]);
  }

  markAllAsRead() {
    const notifiche = this.notifiche.getValue();
    if (notifiche.length === 0 || notifiche.every((notification) => notification.read)) {
      return;
    }

    this.notifiche.next(
      notifiche.map((notification) => ({...notification, read: true}))
    );
  }

  markAsRead(notificationId: number) {
    const notifiche = this.notifiche.getValue();
    const hasUnread = notifiche.some((notification) => notification.id === notificationId && !notification.read);
    if (!hasUnread) {
      return;
    }

    this.notifiche.next(
      notifiche.map((notification) =>
        notification.id === notificationId ? {...notification, read: true} : notification
      )
    );
  }

  message(label: string | StructuredNotificationMessage, color: NotificationColor, showPopup = false) {
    this.addNotification(label, color, showPopup);
  }

  // Il popup resta opt-in: un toast a ogni salvataggio riuscito coprirebbe la UI. Va attivato solo
  // dove il messaggio è l'unico riscontro dell'operazione e la pagina cambia subito dopo, altrimenti
  // resta leggibile solo nel centro notifiche — che l'utente apre più tardi, fuori contesto.
  messageInfo(label: string | StructuredNotificationMessage, showPopup = false) {
    this.message(label, 'success', showPopup);
  }

  messageError(label: string | StructuredNotificationMessage) {
    this.message(label, 'danger', true);
  }

  messageWarning(label: string | StructuredNotificationMessage) {
    this.message(label, 'warning', true);
  }

  runPopupQueue() {
    if (this.popupTimeout) {
      clearTimeout(this.popupTimeout);
    }

    if (this.notificheAlert.getValue().length === 0) {
      return;
    }

    this.popupTimeout = setTimeout(() => {
      this.notificheAlert.next([]);
      this.popupTimeout = undefined;
    }, 4000);
  }

  private addNotification(label: string | StructuredNotificationMessage, color: NotificationColor, showPopup = false) {
    const content = typeof label === 'string'
      ? {message: label}
      : label;

    const notification: NotificationItem = {
      id: this.nextNotificationId++,
      message: content.message,
      details: content.details,
      route: content.route,
      color,
      dateTime: new Date(),
      read: false
    };

    this.notifiche.next([notification, ...this.notifiche.getValue()]);

    if (!showPopup) {
      return;
    }

    this.notificheAlert.next([notification]);
    this.runPopupQueue();
  }
}
